import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit} from '@angular/core';
import {Subject} from 'rxjs/Subject';
import {select, Store} from '@ngrx/store';
import {takeUntil} from 'rxjs/operators';
import {selectTPNoteState} from '@ispm-client/modules/pathway-selections/store/selectors/pathway-selections.selector';
import {GetExecutionPathAction} from '@ispm-client/modules/pathway-selections/store/actions/pathway-selections.actions';
import {DynamicModalLoaderService} from '@ispm/layout/services/dynamic-modal-loader.service';
import {ModalTypes} from '@ispm/layout/components/modals/contants/modal-types.enum';
import {PlaceholderModalComponent} from '@ispm/layout/components/modals/placeholder-modal/placeholder-modal.component';
import {PlaceholderModalLoaderPayload} from '@ispm/layout/classes/placeholder-modal-loader-payload';
import {FormHandlerService} from '@ispm/dynamic-form/services/form-handler.service';
import {GeneralDialogComponent} from '@ispm-client/modules/pathway-selections/general-dialog/general-dialog.component';
import {Widget} from '@ispm/widgets/decorator/widget.decorator';
import {IWidgetItem} from '@ispm/widgets/interfaces/widget-item.interface';
import {IdynamicFormItem} from '@ispm/dynamic-form/interfaces/IdynamicFormItem';
import {get} from 'lodash';
import {INotePopUpConfig} from '@ispm/therapies/interfaces/note-popup.interface';
import {ResolvableService} from '@ispm/core/services/resolvable.service';
import {TranslateService} from "@ngx-translate/core";
import {TestService} from '@ispm/dashboard/test/services/test.service';

/*
   This component used for handling notes , it can be configured to cut note text according to given configuration
   Adding "... Read" More link and showing a pop up once clicked
 */
@Component({
  selector: 'gen-smart-note',
  templateUrl: './smart-note.component.html',
  styleUrls: ['./smart-note.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

@Widget('TPNote')
export class SmartNoteComponent implements OnInit, OnDestroy, IWidgetItem {
  public plainTextNote: string = '';
  public fullFormattedNote: string = '';
  public slicedNote: string = '';
  public displayNote = '';
  @Input() notePopUp: INotePopUpConfig;
  @Input()
  public noteTitle;
  public noteTitleClass: string = '';
  public widgetData: any;
  public widgetCongif;
  private destroyed$: Subject<any> = new Subject();
  private HTML_TAGS_REGX = /<[^>]*>/g;
  private WHITE_SPACES_EQUENCE_REGEX = /\s+>/g;
  private DEFAULT_SLICE_LENGTH = 300;
  testConfiguration: any;
  testViewVerticalLayout: boolean;
  private EXTRA_SPACE_FOR_READ_MORE_BUTTON = 40;
  private ELLIPSIS = '...';
  private MINIMAL_FORMATTING_TAGS_LENGTH = '<p></p>'.length;

  constructor(private store$: Store<any>, private cd: ChangeDetectorRef
    , private popupLoader: DynamicModalLoaderService,
              private _formHandler: FormHandlerService,
              private translateService: TranslateService,
              private testService: TestService) {
  }

  set widgetConfig(widgetConfig: IdynamicFormItem<INotePopUpConfig>) {
    this.widgetCongif = get(widgetConfig, 'widgetPayload', {});
    this.notePopUp = this.widgetCongif.notePopUpConfig;
    this.noteTitle = this.widgetCongif.noteTitle;
    this.noteTitleClass = !!this.widgetCongif.noteTitleClass ? this.widgetCongif.noteTitleClass : '';
  }

  ngOnInit() {
    this.testConfiguration = this.testService.testConfiguration;
    this.testViewVerticalLayout = !!this.testConfiguration && !!this.testConfiguration.testViewVerticalLayout;
    this.store$.pipe(
      select(selectTPNoteState),
      takeUntil(this.destroyed$.asObservable())
    ).subscribe(txt => this.updateNoteDetails(txt));
    this.store$.dispatch(new GetExecutionPathAction({}));
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  updateNoteDetails(txt: string) {
    this.fullFormattedNote = txt;
    this.plainTextNote = this.removeFormatting(this.fullFormattedNote);
    if (this.plainTextNote && this.notePopUp) {
      this.notePopUp.sliceLength = this.notePopUp.sliceLength ? this.notePopUp.sliceLength : this.DEFAULT_SLICE_LENGTH;
      this.slicedNote = this.sliceNote(this.plainTextNote);
      this.notePopUp.popUpIfTextIsTooLong = this.isPopUoNeeded();
    }

    this.displayNote = this.isNoteSliced() ? this.slicedNote + this.ELLIPSIS : this.plainTextNote;
    this.cd.detectChanges();
  }

  sliceNote(note: string): string {
    if (note && this.notePopUp) {
      let slicedNoteLength = Math.min(this.notePopUp.sliceLength - this.EXTRA_SPACE_FOR_READ_MORE_BUTTON, note.length);
      return note.slice(0, slicedNoteLength);
    }
    return note;
  }

  isOldVersion(): boolean {
    return !this.notePopUp;
  }

  removeFormatting(formattedNote: string): string {
    return this.decodeHtml(formattedNote.replace(this.HTML_TAGS_REGX, ' ').replace(this.WHITE_SPACES_EQUENCE_REGEX, ' '));
  }

  decodeHtml(html) {
    let txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
  }

  isPopUoNeeded(): boolean {
    return this.plainTextNote && this.notePopUp && (this.isNoteSliced() || this.isRichTextNote());
  }

  isRichTextNote(): boolean {
    let originalLength = this.fullFormattedNote.length;
    let plainTextLength = this.fullFormattedNote.replace(this.HTML_TAGS_REGX, '').length;
    return originalLength - plainTextLength > this.MINIMAL_FORMATTING_TAGS_LENGTH;
  }

  isNoteSliced(): boolean {
    return this.notePopUp && this.plainTextNote.length > this.slicedNote.length;
  }

  openPopUp() {
    const popUpCompToLoad = this.getComponentToLoad(this.notePopUp.popUpComponent);
    const popUpPayload = this.buildPayload(popUpCompToLoad);
    return this.popupLoader.popupModal(PlaceholderModalComponent, popUpPayload
      , ModalTypes.NONE);
  }

  buildPayload(popUpCompToLoad): PlaceholderModalLoaderPayload<any> {
    const NOTE_CHANNEL_NAME = 'Note_Channel';
    const cssClass = !this.notePopUp.popUpCssClass ||
    this.notePopUp.popUpCssClass === 'fixed-size-modal-container-edit' ||
    this.notePopUp.popUpCssClass === 'fixed-size-modal-container' ?
      'default-smart-notes-modal-container' : this.notePopUp.popUpCssClass;
    const payload = PlaceholderModalLoaderPayload.builder()
      .setComponentToLoad(popUpCompToLoad)
      .setTitle(this.notePopUp.popUpTitle)
      .setCssClasses(cssClass)
      .setPayload({
        formData: this.notePopUp.takeDataFromWidget ? this.widgetData : this.fullFormattedNote, isEditMode: false,
        channelName: NOTE_CHANNEL_NAME, titleCss: this.notePopUp.popUpTitleCss
      })
      .setDeclineButton(this.notePopUp.popUpCancelBtn ? this.notePopUp.popUpCancelBtn : this.translateService.instant('CLOSE_BUTTON'));
    if (this.notePopUp.popUpApproveBtn) {
      payload.setApproveButton(this.notePopUp.popUpApproveBtn);
    }

    return payload;
  }

  getComponentToLoad(componentName): any {
    // component to load inside the popup
    return componentName ? ResolvableService.Resolve(componentName) : GeneralDialogComponent;
  }
}
